// ── Crazy Cards: the fairground after dark. Ferris wheel turning, striped
//    tents, bunting bulbs, searchlights and confetti on the wind. ──

import { useMemo, useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import { useParallax, useAmbientBeat, usePrefersReducedMotion, seeded } from '../../lib/battle3d';

const NEON = ['#f472b6', '#a855f7', '#38bdf8', '#fbbf24', '#4ade80'];

const FerrisWheel = ({ size, spokes = 16 }) => (
  <svg viewBox="-110 -110 220 240" style={{ width: size, height: size * 1.09, overflow: 'visible' }}>
    {/* pylons */}
    <path d="M-42 126 L-6 6 L6 6 L42 126" fill="none" stroke="#1a0d33" strokeWidth="7" strokeLinecap="round" />
    <line x1="-26" y1="86" x2="26" y2="86" stroke="#1a0d33" strokeWidth="5" />
    <motion.g
      animate={{ rotate: 360 }}
      transition={{ duration: 34, repeat: Infinity, ease: 'linear' }}
      style={{ transformOrigin: '0px 0px' }}
    >
      <circle r="96" fill="none" stroke="#2a1550" strokeWidth="4" />
      <circle r="62" fill="none" stroke="#2a1550" strokeWidth="2.5" />
      {Array.from({ length: spokes }, (_, i) => {
        const a = (i / spokes) * Math.PI * 2;
        const x = Math.cos(a) * 96, y = Math.sin(a) * 96;
        const c = NEON[i % NEON.length];
        return (
          <g key={i}>
            <line x1="0" y1="0" x2={x} y2={y} stroke="#2a1550" strokeWidth="2" />
            {/* gondola */}
            <rect x={x - 7} y={y - 3} width="14" height="11" rx="3" fill="#1a0d33" stroke={c} strokeWidth="1.4" />
            <motion.circle
              cx={x} cy={y} r="3.6" fill={c}
              animate={{ opacity: [0.35, 1, 0.35] }}
              transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.1 }}
            />
          </g>
        );
      })}
      <circle r="9" fill="#1a0d33" stroke="#a855f7" strokeWidth="2.5" />
    </motion.g>
  </svg>
);

const Tents = () => (
  <svg viewBox="0 0 1000 150" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
    {[90, 300, 520, 760, 950].map((x, i) => {
      const w = 74 + (i % 3) * 22;
      const c = NEON[(i + 2) % NEON.length];
      return (
        <g key={i}>
          <path d={`M${x - w} 150 L${x - w} 104 Q${x} 26 ${x + w} 104 L${x + w} 150 Z`} fill="#160a2c" />
          {/* stripes */}
          {Array.from({ length: 5 }, (_, j) => (
            <path key={j}
              d={`M${x - w + (j * 2 * w) / 5} 150 L${x - w * 0.2 + (j * 0.4 * w) / 5} 52 L${x - w * 0.2 + ((j + 1) * 0.4 * w) / 5} 52 L${x - w + ((j + 1) * 2 * w) / 5} 150 Z`}
              fill={c} opacity={j % 2 ? 0.26 : 0.08} />
          ))}
          <motion.circle cx={x} cy="24" r="4.5" fill={c}
            animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2 + i * 0.3, repeat: Infinity }} />
          <path d={`M${x} 28 L${x} 46`} stroke={c} strokeWidth="2" opacity="0.6" />
        </g>
      );
    })}
  </svg>
);

const Bunting = ({ rows = 2 }) => (
  <svg viewBox="0 0 1000 70" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
    {Array.from({ length: rows }, (_, r) => {
      const y = 12 + r * 24;
      const sag = 18 + r * 5;
      return (
        <g key={r}>
          <path d={`M0 ${y} Q250 ${y + sag} 500 ${y} T1000 ${y}`} fill="none" stroke="rgba(190,160,255,0.35)" strokeWidth="1.2" />
          {Array.from({ length: 26 }, (_, i) => {
            const t = i / 25;
            const bx = t * 1000;
            const by = y + Math.sin(t * Math.PI * 2) * sag * 0.5 + (t < 0.5 ? t : 1 - t) * sag;
            const c = NEON[(i + r) % NEON.length];
            return (
              <motion.circle key={i} cx={bx} cy={by} r="3.4" fill={c}
                animate={{ opacity: [0.2, 1, 0.2] }}
                transition={{ duration: 1.4, repeat: Infinity, delay: (i % 6) * 0.22 }} />
            );
          })}
        </g>
      );
    })}
  </svg>
);

const Funfair = ({ mobile = false, dimmed = false }) => {
  const reduced = usePrefersReducedMotion();
  const { px, py } = useParallax(!reduced);
  const [burst, setBurst] = useState(0);

  useAmbientBeat(() => setBurst(Date.now()), { min: 7000, max: 14000, enabled: !reduced });

  const skyX = useTransform(px, (v) => v * -8);
  const skyY = useTransform(py, (v) => v * -3.5);
  const wheelX = useTransform(px, (v) => v * -24);
  const wheelY = useTransform(py, (v) => v * -10);
  const tentX = useTransform(px, (v) => v * -42);
  const tentY = useTransform(py, (v) => v * -18);
  const groundX = useTransform(px, (v) => v * -72);
  const groundY = useTransform(py, (v) => v * -30);

  const confetti = useMemo(() => {
    const n = reduced ? 0 : mobile ? 18 : 40;
    const rnd = seeded(3131);
    return Array.from({ length: n }, () => ({
      x: rnd() * 100, w: 4 + rnd() * 5, h: 7 + rnd() * 8,
      dur: 7 + rnd() * 8, delay: rnd() * 10, drift: (rnd() - 0.5) * 180,
      spin: rnd() > 0.5 ? 1 : -1, color: NEON[Math.floor(rnd() * NEON.length)],
    }));
  }, [mobile, reduced]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <div style={{ position: 'absolute', inset: 0, background: '#070214' }} />

      {/* Night sky over the fair */}
      <motion.div style={{
        position: 'absolute', top: '-8%', left: '-8%', right: '-8%', height: '80%', x: skyX, y: skyY,
        background: `radial-gradient(ellipse 100% 55% at 50% 100%, rgba(150,60,255,0.32) 0%, transparent 72%),
                     linear-gradient(to bottom, #06010f 0%, #130a2e 55%, #2a1052 100%)`,
      }} />

      {/* Searchlights */}
      {!reduced && [0, 1].map(i => (
        <motion.div key={i}
          animate={{ rotate: i ? [12, -16, 12] : [-14, 14, -14] }}
          transition={{ duration: 11 + i * 3, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', left: i ? '76%' : '22%', bottom: '38%',
            width: mobile ? 90 : 150, height: '70%', transformOrigin: 'bottom center',
            background: `linear-gradient(to top, ${i ? 'rgba(56,189,248,0.22)' : 'rgba(244,114,182,0.22)'}, transparent 78%)`,
            filter: 'blur(10px)', clipPath: 'polygon(42% 100%, 58% 100%, 100% 0, 0 0)',
          }} />
      ))}

      {/* Ferris wheel */}
      <motion.div style={{ position: 'absolute', left: mobile ? '6%' : '12%', bottom: '44%', x: wheelX, y: wheelY, opacity: 0.95 }}>
        <FerrisWheel size={mobile ? 150 : 260} spokes={mobile ? 12 : 16} />
      </motion.div>

      {/* Tents */}
      <motion.div style={{ position: 'absolute', left: '-6%', right: '-6%', bottom: '44%', height: mobile ? '12%' : '17%', x: tentX, y: tentY }}>
        <Tents />
      </motion.div>

      {/* Bunting overhead */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: mobile ? '9%' : '12%', opacity: 0.85 }}>
        <Bunting rows={mobile ? 1 : 2} />
      </div>

      {/* Neon boardwalk */}
      <motion.div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '47%', overflow: 'hidden', x: groundX, y: groundY }}>
        <div style={{
          position: 'absolute', left: '-25%', right: '-25%', top: 0, height: '190%',
          transform: 'perspective(760px) rotateX(61deg)', transformOrigin: 'center top',
          backgroundImage: `
            repeating-linear-gradient(to right, rgba(168,85,247,0.22) 0 2px, transparent 2px 70px),
            repeating-linear-gradient(to bottom, rgba(244,114,182,0.14) 0 1px, transparent 1px 40px),
            radial-gradient(ellipse 52% 55% at 50% 0%, rgba(150,60,255,0.34) 0%, transparent 66%),
            linear-gradient(to bottom, rgba(10,3,24,0.9), rgba(4,1,10,0.99))`,
        }} />
      </motion.div>

      {/* Firework burst */}
      {burst > 0 && !reduced && (
        <div key={burst} style={{ position: 'absolute', left: '50%', top: '22%' }}>
          {Array.from({ length: 22 }, (_, i) => {
            const a = (i / 22) * Math.PI * 2;
            const r = 90 + (i % 4) * 34;
            return (
              <motion.div key={i}
                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                animate={{ x: Math.cos(a) * r, y: Math.sin(a) * r, opacity: 0, scale: 0.3 }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                style={{
                  position: 'absolute', width: 4, height: 4, borderRadius: '50%',
                  background: NEON[i % NEON.length], boxShadow: `0 0 10px ${NEON[i % NEON.length]}`,
                }} />
            );
          })}
        </div>
      )}

      {/* Confetti */}
      {confetti.map((c, i) => (
        <motion.div key={i}
          initial={{ opacity: 0 }}
          animate={{ y: ['-8vh', '106vh'], x: [0, c.drift, c.drift * 0.5], rotate: [0, 360 * c.spin, 720 * c.spin], opacity: [0, 0.95, 0.8, 0] }}
          transition={{ duration: c.dur, repeat: Infinity, delay: c.delay, ease: 'linear' }}
          style={{
            position: 'absolute', left: `${c.x}%`, top: 0, width: c.w, height: c.h,
            background: c.color, borderRadius: 1, boxShadow: `0 0 6px ${c.color}`,
          }} />
      ))}

      {/* Grade + vignette */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 44%, rgba(0,0,0,0.4) 74%, rgba(0,0,0,0.7) 100%)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, transparent 38%, rgba(0,0,0,0.5) 76%, rgba(0,0,0,0.85) 100%)' }} />
      {dimmed && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />}
    </div>
  );
};

export default Funfair;
